import { ChangeDetectorRef, Component, HostBinding, OnDestroy, OnInit, inject } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { fromEvent, Observable, Subscription } from 'rxjs';
import { AppSelectors } from './store/app/app.selectors';
import { Store } from '@ngxs/store';
import { CollapseSideNav, OpenSideNav, SetSessionExpired } from './store/app/app.actions';
import { ToastsComponent } from './shared/components/toasts/toasts.component';
import { LoginModalPaysComponent } from './shared/components/modals/login/login-modal.component';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { OffcanvasService } from './_services/offcanvas-service/offcanvas.service';
import { IntercomService } from './_services/intercom-service/intercom.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, ToastsComponent],
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit, OnDestroy {
  readonly store = inject(Store);
  readonly modalService = inject(NgbModal);
  readonly router = inject(Router);
  readonly offcanvasService = inject(OffcanvasService);
  private readonly cdr = inject(ChangeDetectorRef);

  @HostBinding('class.sidenav-open') sideNavOpen = true;
  @HostBinding('class.main-content-overflow-hidden') shouldDisableBodyScroll = false;

  resizeObservable$: Observable<Event> = new Observable<Event>();
  resizeSubscription$: Subscription | undefined;
  sessionExpiredSubscription$: Subscription | undefined;
  windowWidth: number = 0;
  private activeLoginModal: NgbModalRef | null = null;

  constructor() {
    // Eagerly inject (without storing a reference) so IntercomService's constructor
    // runs and wires up its store/router subscriptions on app start — it's a
    // `providedIn: 'root'` singleton that is never otherwise injected anywhere.
    inject(IntercomService);
  }

  ngOnInit(): void {
    // Check for side nav being open
    this.store.select(AppSelectors.sideNavOpen).subscribe({
      next: (res) => {
        this.sideNavOpen = res;
        // Trigger change detection after updating host binding
        this.cdr.detectChanges();
      },
    });

    // Check if the body scroll should be disabled (when overlays are shown)
    this.store.select(AppSelectors.shouldDisableBodyScroll).subscribe({
      next: (res) => {
        // Use setTimeout to defer the update to the next change detection cycle
        setTimeout(() => {
          this.shouldDisableBodyScroll = res;
        });
      },
    });

    // Check for auth session expired
    this.sessionExpiredSubscription$ = this.store.select(AppSelectors.sessionExpired).subscribe((expired) => {
      if (expired) {
        this.handleSessionExpired();
      }
    });

    // Close side-nav as default if we're on mobile or smaller screen.
    const ua = navigator.userAgent;
    this.windowWidth = window.innerWidth;
    if (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(ua) &&
      this.windowWidth <= 992
    ) {
      this.store.dispatch(new CollapseSideNav());
    }
    if (this.windowWidth <= 992) {
      this.store.dispatch(new CollapseSideNav());
    }

    // Collapse side nav on resize for smaller screens.
    this.resizeObservable$ = fromEvent(globalThis, 'resize');
    this.resizeSubscription$ = this.resizeObservable$.subscribe(() => {
      this.windowWidth = window.innerWidth;
      if (this.windowWidth <= 992) {
        this.store.dispatch(new CollapseSideNav());
      } else {
        this.store.dispatch(new OpenSideNav());
      }
    });
  }

  ngOnDestroy(): void {
    // Clean up subscriptions to prevent memory leaks
    this.resizeSubscription$?.unsubscribe();
    this.sessionExpiredSubscription$?.unsubscribe();

    // Close any open modal
    if (this.activeLoginModal) {
      this.activeLoginModal.dismiss();
      this.activeLoginModal = null;
    }
  }

  private handleSessionExpired(): void {
    const currentPath = globalThis.location.pathname;

    // Don't show modal if already on auth pages
    if (this.isOnAuthPage(currentPath)) {
      this.store.dispatch(new SetSessionExpired(false));
      return;
    }

    // Prevent multiple modals from opening
    if (this.activeLoginModal) {
      return;
    }

    this.offcanvasService.close();

    // Defer modal opening to the next tick
    setTimeout(() => this.openSessionExpiredModal());
  }

  private isOnAuthPage(currentPath: string): boolean {
    return currentPath.includes('/login') || currentPath.includes('/sign-up') || this.router.url.includes('/login');
  }

  private openSessionExpiredModal(): void {
    // Close any open offcanvas to prevent focus trap
    this.offcanvasService.close();

    this.activeLoginModal = this.modalService.open(LoginModalPaysComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
    });

    // Handle successful login (modal closed)
    this.activeLoginModal.closed.subscribe({
      next: () => this.handleModalClosed(),
    });

    // Handle logout/cancel (modal dismissed)
    this.activeLoginModal.dismissed.subscribe({
      next: () => this.handleModalDismissed(),
    });
  }

  private handleModalClosed(): void {
    this.activeLoginModal = null;
    this.store.dispatch(new SetSessionExpired(false));

    // Reload the current page's data by navigating to the same route
    // This triggers component lifecycle hooks (ngOnInit) to refetch data
    const currentUrl = this.router.url;
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigateByUrl(currentUrl).then(() => {});
    });
  }

  private handleModalDismissed(): void {
    this.activeLoginModal = null;
    this.store.dispatch(new SetSessionExpired(false));
    // User chose to log out, no need to reload
  }
}
